import React, { useState, useEffect, useRef } from 'react';
import { Download, Play, Pause, Volume2, Lock, Calendar } from 'lucide-react';
import { api } from '../services/api';

const SFX_CATEGORIES = [
  { id: 'all', name: 'Todas as Categorias', icon: '🎵' },
  { id: 'apple', name: 'Apple', icon: '🍎' },
  { id: 'bells', name: 'Bells', icon: '🔔' },
  { id: 'cameras', name: 'Cameras', icon: '📷' },
  { id: 'cinematics', name: 'Cinematics', icon: '🎬' },
  { id: 'computer', name: 'Computer', icon: '💻' },
  { id: 'diversos', name: 'Diversos', icon: '🎵' },
  { id: 'gears', name: 'Gears', icon: '⚙️' },
  { id: 'hits-impacts', name: 'Hits - Impacts', icon: '💥' },
  { id: 'ihphone', name: 'iPhone', icon: '📱' },
  { id: 'pop-click', name: 'Pop - Click (Bounce)', icon: '🎯' },
  { id: 'risers', name: 'Risers', icon: '📈' },
  { id: 'ui', name: 'UI', icon: '🖱️' },
  { id: 'wooshes', name: 'Wooshes', icon: '💨' }
];

const SFXView = () => {
  const [sfxList, setSfxList] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [playingId, setPlayingId] = useState(null);
  const [volumes, setVolumes] = useState({});
  const [hasAccess, setHasAccess] = useState(true);
  const [daysRemaining, setDaysRemaining] = useState(null);
  
  const audioRefs = useRef({});

  useEffect(() => {
    loadSFX();
    loadCategories();
  }, [selectedCategory]);

  const loadSFX = async () => {
    try {
      setLoading(true);
      setError('');
      
      const filters = selectedCategory !== 'all' ? { category: selectedCategory } : {};
      const data = await api.getSFX(filters);
      
      setSfxList(data);
      setHasAccess(true);
      
    } catch (err) {
      console.error('Erro ao carregar SFX:', err);
      
      setError('Erro ao carregar efeitos sonoros. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const data = await api.getSFXCategories();
      setCategories(data);
    } catch (err) {
      console.error('Erro ao carregar categorias:', err);
    }
  };

  const togglePlay = (sfxId, audioUrl) => {
    const audio = audioRefs.current[sfxId];
    
    if (!audio) {
      const newAudio = new Audio(audioUrl);
      newAudio.volume = volumes[sfxId] || 0.7;
      audioRefs.current[sfxId] = newAudio;
      
      newAudio.addEventListener('ended', () => {
        setPlayingId(null);
      });
      
      newAudio.play();
      setPlayingId(sfxId);
      
      // Track play
      api.post(`/sfx/${sfxId}/play`).catch(console.error);
      
      return;
    }
    
    if (playingId === sfxId) {
      audio.pause();
      setPlayingId(null);
    } else {
      // Pausa todos os outros
      Object.keys(audioRefs.current).forEach(id => {
        if (id !== sfxId && audioRefs.current[id]) {
          audioRefs.current[id].pause();
        }
      });
      
      audio.play();
      setPlayingId(sfxId);
      
      // Track play
      api.trackSFXPlay(sfxId).catch(console.error);
    }
  };

  const handleVolumeChange = (sfxId, volume) => {
    setVolumes(prev => ({ ...prev, [sfxId]: volume }));
    
    const audio = audioRefs.current[sfxId];
    if (audio) {
      audio.volume = volume;
    }
  };

  const handleDownload = async (sfx) => {
    try {
      await api.downloadSFX(sfx.id);
    } catch (err) {
      console.error('Erro ao fazer download:', err);
      alert('Erro ao baixar SFX. Tente novamente.');
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getCategoryCount = (categoryId) => {
    if (categoryId === 'all') return sfxList.length;
    const cat = categories.find(c => c.id === categoryId);
    return cat?.count || 0;
  };

  // Se não tem acesso, mostra tela de bloqueio
  if (!hasAccess) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-gradient-to-br from-purple-500/10 to-pink-500/10 backdrop-blur-xl rounded-3xl border border-white/10 p-8 text-center">
          <div className="w-20 h-20 bg-purple-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <Lock className="w-10 h-10 text-purple-400" />
          </div>
          
          <h2 className="text-2xl font-bold text-white mb-3">
            Conteúdo Premium Bloqueado
          </h2>
          
          <p className="text-slate-300 mb-6">
            O acesso aos efeitos sonoros (SFX) é liberado após <strong>7 dias</strong> de assinatura ativa.
          </p>
          
          {daysRemaining !== null && (
            <div className="bg-white/5 rounded-xl p-4 mb-6">
              <Calendar className="w-6 h-6 text-purple-400 mx-auto mb-2" />
              <p className="text-slate-400 text-sm">
                Faltam <strong className="text-white">{daysRemaining} dias</strong> para liberar
              </p>
            </div>
          )}
          
          <p className="text-slate-400 text-sm">
            Continue aproveitando as cenas cinematográficas enquanto isso! 🎬
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="pb-8">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2">Efeitos Sonoros (SFX)</h1>
        <p className="text-slate-400">
          Biblioteca premium de efeitos sonoros para seus projetos
        </p>
      </div>

      {/* Dropdown de Categorias */}
      <div className="mb-8">
        <label className="block text-slate-400 text-sm font-bold uppercase mb-3">
          Filtrar por Categoria
        </label>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
          {SFX_CATEGORIES.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              className={`p-4 rounded-xl border font-semibold text-sm transition-all ${
                selectedCategory === cat.id
                  ? 'bg-gradient-to-br from-purple-500 to-pink-500 border-transparent text-white shadow-lg shadow-purple-500/30'
                  : 'bg-white/5 border-white/10 text-slate-400 hover:text-white hover:border-white/20 hover:bg-white/10'
              }`}
            >
              <span className="text-2xl block mb-2">{cat.icon}</span>
              <span className="block truncate">{cat.name}</span>
              {selectedCategory === cat.id && (
                <span className="block text-xs text-purple-200 mt-1">
                  {getCategoryCount(cat.id)} SFX
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
          <p className="text-slate-400 mt-4">Carregando SFX...</p>
        </div>
      )}

      {/* Error */}
      {error && !loading && (
        <div className="bg-red-500/10 border border-red-500/50 rounded-xl p-4 text-center">
          <p className="text-red-400">{error}</p>
        </div>
      )}

      {/* Grid de SFX */}
      {!loading && !error && sfxList.length === 0 && (
        <div className="text-center py-12">
          <p className="text-slate-400">Nenhum SFX encontrado nesta categoria.</p>
        </div>
      )}

      {!loading && !error && sfxList.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {sfxList.map((sfx) => (
            <div
              key={sfx.id}
              className="bg-gradient-to-br from-white/[0.07] to-white/[0.02] backdrop-blur-xl rounded-2xl border border-white/10 p-5 hover:border-purple-500/30 transition-all group"
            >
              {/* Título */}
              <div className="mb-4">
                <h3 className="text-white font-bold text-lg mb-1 line-clamp-1">
                  {sfx.title}
                </h3>
                <div className="flex items-center gap-2 text-xs text-slate-400">
                  <span className="bg-purple-500/20 text-purple-300 px-2 py-1 rounded-full">
                    {SFX_CATEGORIES.find(c => c.id === sfx.category)?.name || sfx.category}
                  </span>
                  <span>{formatDuration(sfx.duration)}</span>
                  {sfx.format && <span className="uppercase">{sfx.format}</span>}
                </div>
              </div>

              {/* Tags */}
              {sfx.tags && sfx.tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-4">
                  {sfx.tags.slice(0, 3).map((tag, idx) => (
                    <span
                      key={idx}
                      className="text-xs bg-white/5 text-slate-300 px-2 py-1 rounded"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}

              {/* Controles de Áudio */}
              <div className="space-y-3">
                {/* Play/Pause + Volume */}
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => togglePlay(sfx.id, sfx.audio_url)}
                    className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 rounded-xl flex items-center justify-center transition-all shadow-lg shadow-purple-500/30 group-hover:shadow-purple-500/50 flex-shrink-0"
                  >
                    {playingId === sfx.id ? (
                      <Pause className="w-5 h-5 text-white" />
                    ) : (
                      <Play className="w-5 h-5 text-white ml-0.5" />
                    )}
                  </button>

                  {/* Volume Control */}
                  <div className="flex-1 flex items-center gap-2">
                    <Volume2 className="w-4 h-4 text-slate-400 flex-shrink-0" />
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.01"
                      value={volumes[sfx.id] || 0.7}
                      onChange={(e) => handleVolumeChange(sfx.id, parseFloat(e.target.value))}
                      className="flex-1 h-2 bg-white/10 rounded-full appearance-none cursor-pointer"
                      style={{
                        background: `linear-gradient(to right, rgb(168 85 247) 0%, rgb(236 72 153) ${(volumes[sfx.id] || 0.7) * 100}%, rgb(255 255 255 / 0.1) ${(volumes[sfx.id] || 0.7) * 100}%)`
                      }}
                    />
                  </div>
                </div>

                {/* Download Button */}
                <button
                  onClick={() => handleDownload(sfx)}
                  className="w-full bg-white/5 hover:bg-white/10 border border-white/10 hover:border-purple-500/50 text-white py-3 rounded-xl transition-all flex items-center justify-center gap-2 font-semibold text-sm"
                >
                  <Download className="w-4 h-4" />
                  Download
                </button>
              </div>

              {/* Stats */}
              <div className="mt-4 pt-4 border-t border-white/5 flex items-center justify-between text-xs text-slate-500">
                <span>{sfx.play_count || 0} plays</span>
                <span>{sfx.download_count || 0} downloads</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SFXView;
